import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import { SITUATION_LABELS, STATE_LABELS } from '../../../core/occurrence/occurrence.labels';
import { OccurrenceSituation, OccurrenceState } from '../../../core/occurrence/occurrence.models';

interface PublicMessage {
  readonly fromUs: boolean;
  readonly body: string;
  readonly sentAt: string;
}

interface PublicConversation {
  readonly occurrenceCode: string;
  readonly subject: string;
  readonly situation: OccurrenceSituation;
  readonly state: OccurrenceState;
  readonly open: boolean;
  readonly messages: readonly PublicMessage[];
}

/**
 * A conversa vista de fora, aberta pelo link que segue em cada e-mail.
 *
 * <p>Quem está do outro lado não tem conta: o token do endereço é a credencial, e o que se vê
 * é só o que foi trocado com essa pessoa — nunca o conteúdo do registo nem as notas internas.
 */
@Component({
  selector: 'app-occurrence-conversation',
  imports: [FormsModule, DatePipe, RouterLink],
  templateUrl: './occurrence-conversation.html',
})
export class OccurrenceConversation implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  protected readonly tenant = this.route.snapshot.paramMap.get('tenant') ?? '';
  private readonly token = this.route.snapshot.paramMap.get('token') ?? '';

  protected readonly conversation = signal<PublicConversation | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly sending = signal(false);
  protected readonly sent = signal(false);
  protected reply = '';

  protected readonly situationLabels = SITUATION_LABELS;
  protected readonly stateLabels = STATE_LABELS;

  private url(): string {
    return `/api/t/${this.tenant}/publico/conversas/${encodeURIComponent(this.token)}`;
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.http.get<PublicConversation>(this.url()).subscribe({
      next: (conversation) => {
        this.conversation.set(conversation);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Este link não está válido. Confirme se o copiou por inteiro.');
        this.loading.set(false);
      },
    });
  }

  protected send(): void {
    if (!this.reply.trim()) return;
    this.sending.set(true);
    this.error.set(null);
    this.http.post(this.url(), { body: this.reply.trim() }).subscribe({
      next: () => {
        this.reply = '';
        this.sending.set(false);
        this.sent.set(true);
        this.load();
      },
      error: (response: HttpErrorResponse) => {
        this.sending.set(false);
        this.error.set((response.error as ApiError)?.message ?? 'Não foi possível enviar a mensagem.');
      },
    });
  }
}
